import React, { useState, useEffect } from 'react';
import { setPageTitle } from '../hooks/usePageTitle';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';
import ActivityLog from '../components/ActivityLog';
import moment from 'moment';
import TagSelector from '../components/TagSelector';

const EditListingPage = () => {
  const {
    API, BACKEND_URL, activeTab, appointments, availableTags, calculateAppointmentPricing,
    clientAvatarUrls, clients, darkMode, dragOverIndex, draggingIndex, fetchAppointmentMedia, fetchSettings,
    formatTableDate, formatTableTime, handleDragEnter, handleDragLeave, handleDragOver,
    handleDragStart, handleDrop, handleUploadClick, imageThumbnailSize, listingTasksExpanded,
    mediaExpanded, mediaFiles, moment, products, propertyClients, propertyDetailsExpanded,
    saveMediaOrder, selectedListingForEdit, setActiveTab, setAppointmentClientSearchQuery,
    setAppointmentForm, setAppointments, setDragOverIndex, setDraggingIndex, setEditAddressForm,
    setEditingAppointment, setEditingTask, setImageThumbnailSize, setListingTasksExpanded,
    setMediaExpanded, setMediaManagerAppointment, setMediaToDelete, setPropertyClients,
    setPropertyDetailsExpanded, setSelectedAppointment, setSelectedClientToAdd,
    setSelectedListingForEdit, setSelectedProducts, setShowAddClientModal, setShowAppointmentForm,
    setShowDeleteConfirm, setShowEditAddressModal, setShowMediaUpload, setShowTaskForm,
    setSlideshowIndex, setSlideshowOpen, setTaskForm, setUploadProgress, showMediaUpload,
    showToast, tasks, teamMemberAvatarUrls, teamMembers, updateTaskStatus, uploadProgress, editTask, editClient,
  } = useDashboard();

  const [mediaTab, setMediaTab] = useState('images');
  const IMAGES_INITIAL_VISIBLE = 12; // ~2 rows at default thumbnail size
  const [visibleImagesCount, setVisibleImagesCount] = useState(IMAGES_INITIAL_VISIBLE);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null); // null | { loaded, total, percent, phase }

  // Exit bulk mode and clear selection whenever the user switches tabs.
  useEffect(() => {
    setBulkMode(false);
    setSelectedIds(new Set());
  }, [mediaTab]);

  // Dynamic page title — show the property address (or shortened version) in
  // the browser tab so power users with multiple listings open can scan them.
  // NOTE: reads selectedListingForEdit directly (the source prop) so this
  // effect can sit above the `const listing = selectedListingForEdit` alias
  // without hitting a temporal-dead-zone ReferenceError.
  useEffect(() => {
    if (selectedListingForEdit?.property_address) {
      // Use just the street portion when comma-separated, otherwise the full address
      const addr = selectedListingForEdit.property_address.split(',')[0].trim() || selectedListingForEdit.property_address;
      setPageTitle(`${addr} | Listing`);
    } else {
      setPageTitle('Edit Listing');
    }
    return () => setPageTitle();
  }, [selectedListingForEdit?.property_address]);

  const toggleBulkMode = () => {
    setBulkMode((m) => {
      if (m) setSelectedIds(new Set());
      return !m;
    });
  };

  const toggleImageSelected = (mediaId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) next.delete(mediaId); else next.add(mediaId);
      return next;
    });
  };

  // Resolve the active tab's collection at call time so closures see the
  // latest images/videos/files arrays. Floor Plans and 3D Tours are URL-based
  // and don't support bulk-actions.
  const getActiveItems = () => (
    mediaTab === 'videos' ? videos
      : mediaTab === 'files' ? files
      : images
  );
  const getVisibleActiveItems = () => (
    mediaTab === 'images' ? images.slice(0, visibleImagesCount) : getActiveItems()
  );

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllVisible = () => {
    setSelectedIds(new Set(getVisibleActiveItems().map((m) => m.id)));
  };

  const handleBulkDownload = async () => {
    const targets = getActiveItems().filter((m) => selectedIds.has(m.id));
    if (targets.length === 0) return;
    setBulkBusy(true);
    setDownloadProgress({ loaded: 0, total: 0, percent: 0, phase: 'preparing' });
    try {
      const token = localStorage.getItem('token');
      const slug = listing?.property_address
        ? listing.property_address.replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 60)
        : 'media';
      const zipName = `${slug}-${mediaTab}.zip`;
      const response = await axios.post(
        `${API}/media/bulk-download-zip`,
        { media_ids: targets.map((m) => m.id), zip_filename: zipName },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          onDownloadProgress: (evt) => {
            const loaded = evt.loaded || 0;
            const total = evt.total || 0;
            const percent = total ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
            setDownloadProgress({ loaded, total, percent, phase: 'downloading' });
          },
        }
      );
      const added = parseInt(response.headers['x-zip-file-count'] || `${targets.length}`, 10);
      const skipped = parseInt(response.headers['x-zip-skipped-count'] || '0', 10);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      const noun = mediaTab === 'videos' ? 'video' : mediaTab === 'files' ? 'file' : 'image';
      const pluralNoun = mediaTab === 'videos' ? 'videos' : mediaTab === 'files' ? 'files' : 'images';
      if (skipped > 0) {
        showToast(`Downloaded ${added} ${pluralNoun}, ${skipped} skipped`, 'success');
      } else {
        showToast(`Downloaded ${added} ${added === 1 ? noun : pluralNoun} as ZIP`, 'success');
      }
    } catch (err) {
      console.error('Bulk download failed', err);
      showToast('Failed to build ZIP download', 'error');
    } finally {
      setBulkBusy(false);
      setDownloadProgress(null);
    }
  };

  const handleBulkDelete = async () => {
    const targets = getActiveItems().filter((m) => selectedIds.has(m.id));
    if (targets.length === 0) return;
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const results = await Promise.allSettled(
        targets.map((m) => axios.delete(`${API}/media/${m.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }))
      );
      const failures = results.filter((r) => r.status === 'rejected').length;
      if (listing) await fetchAppointmentMedia(listing.id);
      const ok = targets.length - failures;
      const noun = mediaTab === 'videos' ? 'video' : mediaTab === 'files' ? 'file' : 'image';
      const pluralNoun = mediaTab === 'videos' ? 'videos' : mediaTab === 'files' ? 'files' : 'images';
      if (failures === 0) {
        showToast(`Deleted ${ok} ${ok === 1 ? noun : pluralNoun}`, 'success');
      } else {
        showToast(`Deleted ${ok}, failed ${failures}`, 'error');
      }
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      setBulkMode(false);
    } catch (err) {
      showToast('Failed to delete images', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const listing = selectedListingForEdit;
  const listingTasks = tasks.filter(t => t.appointment_id === listing?.id);
  const completedTasks = listingTasks.filter(t => t.status === 'completed').length;
  const images = mediaFiles.filter(m => m.mime_type?.startsWith('image/'));
  const videos = mediaFiles.filter(m => m.mime_type?.startsWith('video/'));
  const files = mediaFiles.filter(m => !m.mime_type?.startsWith('image/') && !m.mime_type?.startsWith('video/'));
  const heroImage = images.find(m => m.thumbnailUrl);
  const primaryClient = clients.find(c => c.id === listing?.client_id);

  const mediaCounts = { images: images.length, videos: videos.length, floorPlans: (listing?.floor_plans || []).length, tours: (listing?.tours_3d || []).length, files: files.length };
  const totalMedia = Object.values(mediaCounts).reduce((a, b) => a + b, 0);

  // Theme tokens
  const t = darkMode ? {
    page: '#09090b',
    card: '#121214',
    cardInner: '#18181b',
    border: '#27272a',
    borderHighlight: 'rgba(194,166,122,0.15)',
    text: '#fafafa',
    textSecondary: '#a1a1aa',
    textMuted: '#52525b',
    accent: '#c2a67a',
    accentBg: 'rgba(194,166,122,0.1)',
    glassBg: 'rgba(255,255,255,0.08)',
    glassBorder: 'rgba(255,255,255,0.1)',
    heroOverlay: 'bg-gradient-to-r from-black/85 via-black/50 to-transparent',
    heroBottom: 'bg-gradient-to-t from-[#09090b] via-transparent to-transparent',
    inputBg: '#18181b',
    inputBorder: '#3f3f46',
    hoverBg: 'rgba(255,255,255,0.04)',
    statBg: 'rgba(255,255,255,0.06)',
  } : {
    page: '#f8f9fa',
    card: '#ffffff',
    cardInner: '#f4f4f5',
    border: '#e4e4e7',
    borderHighlight: 'rgba(37,99,235,0.15)',
    text: '#18181b',
    textSecondary: '#52525b',
    textMuted: '#a1a1aa',
    accent: '#2563eb',
    accentBg: 'rgba(37,99,235,0.08)',
    glassBg: 'rgba(255,255,255,0.85)',
    glassBorder: 'rgba(0,0,0,0.08)',
    heroOverlay: 'bg-gradient-to-r from-black/70 via-black/40 to-transparent',
    heroBottom: 'bg-gradient-to-t from-[#f8f9fa] via-transparent to-transparent',
    inputBg: '#ffffff',
    inputBorder: '#d4d4d8',
    hoverBg: 'rgba(0,0,0,0.03)',
    statBg: 'rgba(255,255,255,0.9)',
  };

  const gold = t.accent;

  // Inline bar rendered above each tab's grid while in bulk mode.
  // Hoisted as a helper so the markup stays in one place across tabs.
  const renderBulkActionsBar = () => (
    <div
      data-testid="bulk-actions-bar"
      className="mb-4 px-4 py-3 rounded-lg border"
      style={{ background: t.cardInner, borderColor: t.border }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs">
          <span className="font-medium text-zinc-200" data-testid="bulk-selection-count">
            {selectedIds.size} selected
          </span>
          <button
            onClick={selectAllVisible}
            className="text-zinc-400 hover:text-zinc-100 underline-offset-4 hover:underline"
            data-testid="bulk-select-all-btn"
          >
            Select all visible
          </button>
          {selectedIds.size > 0 && (
            <button
              onClick={clearSelection}
              className="text-zinc-400 hover:text-zinc-100 underline-offset-4 hover:underline"
              data-testid="bulk-clear-selection-btn"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={selectedIds.size === 0 || bulkBusy}
            onClick={handleBulkDownload}
            data-testid="bulk-download-btn"
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {downloadProgress
              ? (downloadProgress.phase === 'preparing'
                  ? 'Preparing ZIP…'
                  : `Downloading… ${downloadProgress.percent}%`)
              : `Download (${selectedIds.size})`}
          </button>
          <button
            disabled={selectedIds.size === 0 || bulkBusy}
            onClick={() => setShowBulkDeleteConfirm(true)}
            data-testid="bulk-delete-btn"
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Delete ({selectedIds.size})
          </button>
        </div>
      </div>

      {downloadProgress && (
        <div className="mt-3" data-testid="bulk-download-progress">
          <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1.5">
            <span>
              {downloadProgress.phase === 'preparing'
                ? 'Server is building your ZIP…'
                : `${(downloadProgress.loaded / (1024 * 1024)).toFixed(1)} MB${
                    downloadProgress.total
                      ? ` / ${(downloadProgress.total / (1024 * 1024)).toFixed(1)} MB`
                      : ''
                  }`}
            </span>
            <span className="tabular-nums font-mono">{downloadProgress.percent}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
            <div
              className={`h-full transition-all duration-150 ${
                downloadProgress.phase === 'preparing' ? 'animate-pulse' : ''
              }`}
              style={{
                width: downloadProgress.phase === 'preparing' ? '15%' : `${downloadProgress.percent}%`,
                background: 'linear-gradient(90deg, #38bdf8, #818cf8)',
              }}
              data-testid="bulk-download-progress-bar"
            />
          </div>
        </div>
      )}
    </div>
  );

  // Reusable header row with size slider (if applicable) + Bulk Actions + Save Order.
  const renderTabToolbar = (opts = {}) => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        {opts.sizeSlider && (
          <>
            <span className="text-xs uppercase tracking-widest text-zinc-500">Size</span>
            <input type="range" min="80" max="240" value={imageThumbnailSize} onChange={(e) => setImageThumbnailSize(parseInt(e.target.value))} className="w-24 accent-[#c2a67a]" />
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={toggleBulkMode}
          data-testid="bulk-actions-toggle-btn"
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
            bulkMode
              ? 'bg-zinc-500/20 text-zinc-200 border-zinc-500/40 hover:bg-zinc-500/30'
              : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20 hover:bg-indigo-500/20'
          }`}
        >
          {bulkMode ? 'Cancel' : 'Bulk Actions'}
        </button>
        <button onClick={saveMediaOrder} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors" data-testid="save-media-order-btn">
          Save Order
        </button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${!darkMode ? 'listing-light' : ''}`} style={{ background: t.page, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Hero Section */}
      <div className="relative h-52 lg:h-60 overflow-hidden rounded-2xl">
        {heroImage ? (
          <img src={heroImage.thumbnailUrl} alt={listing.property_address} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: darkMode ? 'linear-gradient(135deg, #27272a, #18181b)' : 'linear-gradient(135deg, #e4e4e7, #d4d4d8)' }} />
        )}
        {/* Uniform darkening overlay for legibility */}
        <div className="absolute inset-0 bg-black/40" />

        {/* Back */}
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={() => { setActiveTab('media'); setSelectedListingForEdit(null); }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white hover:scale-105 transition-all shadow-lg"
            style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.25)' }}
            data-testid="back-to-listings-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Listings
          </button>
        </div>

        {/* Edit Address */}
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={() => { setEditAddressForm(listing.property_address); setShowEditAddressModal(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
            style={{ background: gold, color: darkMode ? '#1a1a1a' : '#ffffff' }}
            data-testid="edit-address-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            Edit Address
          </button>
        </div>

        {/* Hero Content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between gap-6">
              <div className="flex-1">
                <h1 className="text-3xl lg:text-4xl font-light text-white tracking-tight mb-3">
                  {listing.property_address}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    listing.status === 'completed' ? 'bg-emerald-600 text-white' :
                    listing.status === 'scheduled' ? 'bg-blue-600 text-white' :
                    'bg-zinc-600 text-white'
                  }`} data-testid="listing-status-badge">
                    {listing.status}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: darkMode ? 'rgba(194,166,122,0.85)' : 'rgba(37,99,235,0.85)' }}>
                    {listing.service_type?.replace('_', ' ')}
                  </span>
                  {totalMedia > 0 && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-black/50 text-white backdrop-blur-sm">
                      {totalMedia} files
                    </span>
                  )}
                </div>
                {availableTags.length > 0 && (
                  <div className="mt-3 relative z-20" onClick={(e) => e.stopPropagation()} data-testid="listing-detail-tags">
                    <TagSelector
                      selectedTagIds={listing.tag_ids || []}
                      availableTags={availableTags}
                      onChange={async (newTagIds) => {
                        try {
                          await axios.put(`${API}/appointments/${listing.id}/tags`, { tag_ids: newTagIds });
                          setSelectedListingForEdit(prev => ({ ...prev, tag_ids: newTagIds }));
                          setAppointments(prev => prev.map(a => a.id === listing.id ? { ...a, tag_ids: newTagIds } : a));
                        } catch (err) { showToast('Failed to update tags', 'error'); }
                      }}
                      compact={true}
                    />
                  </div>
                )}
              </div>
              {/* Quick Stats */}
              <div className="hidden lg:flex gap-4">
                {[
                  { label: 'Images', value: mediaCounts.images },
                  { label: 'Videos', value: mediaCounts.videos },
                  { label: 'Tasks', value: `${completedTasks}/${listingTasks.length}` },
                ].map(stat => (
                  <div key={stat.label} className="text-center px-4 py-2 rounded-xl shadow-lg" style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <div className="text-xl font-semibold text-white">{stat.value}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/60">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

          {/* Left Column - Media & Property Details */}
          <div className="xl:col-span-9 space-y-6">

            {/* Media Section */}
            <div className="rounded-xl overflow-visible" style={{ background: t.card, border: `1px solid ${t.border}` }}>
              <div className="flex items-center justify-between p-6 pb-0">
                <h2 className="text-lg font-semibold text-zinc-50 tracking-tight flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Media
                </h2>
                <button
                  onClick={() => { setMediaManagerAppointment(listing); setSelectedAppointment(listing); setShowMediaUpload(true); }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                  style={{ background: gold, color: darkMode ? '#1a1a1a' : '#ffffff' }}
                  data-testid="add-media-btn"
                >
                  Upload Media
                </button>
              </div>

              {/* Media Tabs */}
              <div className="flex gap-1 px-6 pt-4" style={{ borderBottom: `1px solid ${t.border}` }}>
                {[
                  { key: 'images', label: 'Images', count: mediaCounts.images },
                  { key: 'videos', label: 'Videos', count: mediaCounts.videos },
                  { key: 'floorPlans', label: 'Floor Plans', count: mediaCounts.floorPlans },
                  { key: 'tours3d', label: '3D Tours', count: mediaCounts.tours },
                  { key: 'files', label: 'Files', count: mediaCounts.files },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setMediaTab(tab.key)}
                    className={`px-4 py-3 text-sm font-medium transition-colors relative ${
                      mediaTab === tab.key ? 'text-zinc-50' : 'text-zinc-500 hover:text-zinc-300'
                    }`}
                    data-testid={`media-tab-${tab.key}`}
                  >
                    {tab.label}
                    {tab.count > 0 && <span className="ml-1.5 text-xs text-zinc-600">{tab.count}</span>}
                    {mediaTab === tab.key && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: gold }} />}
                  </button>
                ))}
              </div>

              {/* Images Tab */}
              {mediaTab === 'images' && (
                <div className="p-6">
                  {images.length > 0 ? (
                    <>
                      {renderTabToolbar({ sizeSlider: true })}

                      {bulkMode && renderBulkActionsBar()}
                      <div className="flex flex-wrap gap-3">
                        {images.slice(0, visibleImagesCount).map((media, index) => (
                          <div
                            key={media.id}
                            className={`group rounded-lg overflow-hidden transition-all duration-200 ${
                              draggingIndex === mediaFiles.indexOf(media) ? 'opacity-40 scale-105' :
                              dragOverIndex === mediaFiles.indexOf(media) ? 'ring-2 ring-emerald-500 scale-105' :
                              bulkMode && selectedIds.has(media.id) ? 'ring-2 ring-indigo-400 scale-[1.02]' : ''
                            }`}
                            style={{ width: `${imageThumbnailSize}px`, background: t.cardInner, border: `1px solid ${t.border}` }}
                          >
                            <div
                              draggable={!bulkMode}
                              onDragStart={(e) => !bulkMode && handleDragStart(e, mediaFiles.indexOf(media))}
                              onDragOver={(e) => !bulkMode && handleDragOver(e, mediaFiles.indexOf(media))}
                              onDragEnter={(e) => !bulkMode && handleDragEnter(e, mediaFiles.indexOf(media))}
                              onDragLeave={(e) => !bulkMode && handleDragLeave(e)}
                              onDrop={(e) => !bulkMode && handleDrop(e, mediaFiles.indexOf(media))}
                              onDragEnd={() => { setDraggingIndex(null); setDragOverIndex(null); }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (bulkMode) {
                                  toggleImageSelected(media.id);
                                } else {
                                  setSlideshowIndex(index);
                                  setSlideshowOpen(true);
                                }
                              }}
                              data-testid={bulkMode ? `bulk-image-tile-${media.id}` : undefined}
                              className="relative cursor-pointer"
                            >
                              <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-mono z-10">#{index + 1}</div>
                              {bulkMode && (
                                <div
                                  className={`absolute top-1.5 right-1.5 z-20 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                                    selectedIds.has(media.id)
                                      ? 'bg-indigo-500 border-indigo-400'
                                      : 'bg-black/60 border-white/70'
                                  }`}
                                >
                                  {selectedIds.has(media.id) && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </div>
                              )}
                              {!bulkMode && media.hidden && <div className="absolute top-1.5 right-1.5 bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold z-10">Hidden</div>}
                              <img src={media.thumbnailUrl || `${BACKEND_URL}/api/media/${media.id}`} alt={media.original_filename} className={`w-full object-cover transition-opacity ${media.hidden ? 'opacity-30 grayscale' : ''}`} style={{ height: `${imageThumbnailSize}px` }} />
                              {!bulkMode && (
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100">
                                <div className="flex gap-1.5">
                                  <button onClick={(e) => { e.stopPropagation(); const l = document.createElement('a'); l.href = `${BACKEND_URL}/api/media/${media.id}`; l.download = media.original_filename; document.body.appendChild(l); l.click(); document.body.removeChild(l); }} className="p-2 rounded-lg bg-white text-gray-800 hover:bg-gray-100 shadow-md transition-colors" title="Download">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                                  </button>
                                  <button onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const newHidden = !media.hidden;
                                      await axios.put(`${API}/media/${media.id}/visibility`, { hidden: newHidden });
                                      await fetchAppointmentMedia(listing.id);
                                      showToast(newHidden ? 'Image hidden from website' : 'Image visible on website', 'success');
                                    } catch (err) { showToast('Failed to update visibility', 'error'); }
                                  }} className={`p-2 rounded-lg shadow-md transition-colors ${media.hidden ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-white text-gray-800 hover:bg-gray-100'}`} title={media.hidden ? 'Show on website' : 'Hide from website'}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">{media.hidden ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />}</svg>
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setMediaToDelete(media); setShowDeleteConfirm(true); }} className="p-2 rounded-lg bg-red-500 text-white hover:bg-red-600 shadow-md transition-colors" title="Delete">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </div>
                              </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {images.length > IMAGES_INITIAL_VISIBLE && (
                        <div className="flex justify-center mt-6">
                          <button
                            data-testid="toggle-show-more-images-btn"
                            onClick={() => setVisibleImagesCount(visibleImagesCount >= images.length ? IMAGES_INITIAL_VISIBLE : images.length)}
                            className="px-5 py-2 text-xs font-medium uppercase tracking-widest rounded-lg border transition-colors hover:bg-white/5"
                            style={{ borderColor: t.border, color: gold }}
                          >
                            {visibleImagesCount >= images.length
                              ? 'Show less'
                              : `Show more (${images.length - visibleImagesCount})`}
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-12"><p className="text-sm text-zinc-500">No images uploaded yet</p></div>
                  )}
                </div>
              )}

              {/* Videos Tab */}
              {mediaTab === 'videos' && (
                <div className="p-6">
                  {videos.length > 0 ? (
                    <>
                      {renderTabToolbar()}
                      {bulkMode && renderBulkActionsBar()}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {videos.map((media, index) => {
                          const isSelected = selectedIds.has(media.id);
                          return (
                          <div
                            key={media.id}
                            className={`rounded-lg overflow-hidden transition-all ${
                              draggingIndex === mediaFiles.indexOf(media) ? 'opacity-40' :
                              dragOverIndex === mediaFiles.indexOf(media) ? 'ring-2 ring-emerald-500' :
                              bulkMode && isSelected ? 'ring-2 ring-indigo-400 scale-[1.01]' : ''
                            }`}
                            style={{ background: t.cardInner, border: `1px solid ${t.border}` }}
                          >
                            <div
                              draggable={!bulkMode}
                              onDragStart={(e) => !bulkMode && handleDragStart(e, mediaFiles.indexOf(media))}
                              onDragOver={(e) => !bulkMode && handleDragOver(e, mediaFiles.indexOf(media))}
                              onDragEnter={(e) => !bulkMode && handleDragEnter(e, mediaFiles.indexOf(media))}
                              onDragLeave={(e) => !bulkMode && handleDragLeave(e)}
                              onDrop={(e) => !bulkMode && handleDrop(e, mediaFiles.indexOf(media))}
                              onDragEnd={() => { setDraggingIndex(null); setDragOverIndex(null); }}
                              onClick={(e) => {
                                if (bulkMode) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleImageSelected(media.id);
                                }
                              }}
                              data-testid={bulkMode ? `bulk-video-tile-${media.id}` : undefined}
                              className={`relative ${bulkMode ? 'cursor-pointer' : 'cursor-move'}`}
                            >
                              <div className="absolute top-1.5 left-1.5 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-mono z-10">#{index + 1}</div>
                              {bulkMode && (
                                <div
                                  className={`absolute top-1.5 right-1.5 z-20 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                                    isSelected
                                      ? 'bg-indigo-500 border-indigo-400'
                                      : 'bg-black/60 border-white/70'
                                  }`}
                                >
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </div>
                              )}
                              {/* In bulk mode, hide native video controls so the whole tile is a single click target */}
                              {bulkMode ? (
                                <div className="w-full h-40 bg-black flex items-center justify-center">
                                  <svg className="w-10 h-10 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </div>
                              ) : (
                                <video src={`${BACKEND_URL}/api/media/${media.id}?token=${localStorage.getItem('token') || ''}`} className="w-full h-40 object-cover" controls preload="metadata" />
                              )}
                            </div>
                            <div className="p-3 flex items-center justify-between">
                              <p className="text-xs text-zinc-400 truncate flex-1 mr-2">{media.original_filename}</p>
                              {!bulkMode && (
                                <div className="flex gap-1">
                                  <button onClick={() => { const l = document.createElement('a'); l.href = `${BACKEND_URL}/api/media/${media.id}?download=true&token=${localStorage.getItem('token') || ''}`; l.download = media.original_filename; document.body.appendChild(l); l.click(); document.body.removeChild(l); }} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400" title="Download"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg></button>
                                  <button onClick={() => { setMediaToDelete(media); setShowDeleteConfirm(true); }} className="p-1.5 rounded hover:bg-red-500/20 text-zinc-400 hover:text-red-400" title="Delete"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </>
                  ) : <div className="text-center py-12"><p className="text-sm text-zinc-500">No videos uploaded yet</p></div>}
                </div>
              )}

              {/* Floor Plans Tab */}
              {mediaTab === 'floorPlans' && (
                <div className="p-6">
                  {(listing?.floor_plans || []).length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {(listing?.floor_plans || []).map((plan, index) => (
                        <div key={plan.id || index} className="rounded-lg overflow-hidden" style={{ background: t.cardInner, border: `1px solid ${t.border}` }}>
                          {plan.type === 'image' ? (
                            <img src={plan.url.startsWith('http') ? plan.url : `${BACKEND_URL}/api/media/${plan.url}`} alt={plan.name || `Floor Plan ${index + 1}`} className="w-full h-40 object-contain bg-zinc-900" />
                          ) : (
                            <div className="w-full h-40 flex items-center justify-center bg-zinc-900"><svg className="w-12 h-12 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" /></svg></div>
                          )}
                          <div className="p-3 flex items-center justify-between">
                            <p className="text-xs text-zinc-400 truncate">{plan.name || `Floor Plan ${index + 1}`}</p>
                            <button onClick={() => { const updated = (listing?.floor_plans || []).filter((_, i) => i !== index); setSelectedListingForEdit({...listing, floor_plans: updated}); }} className="text-xs text-red-400 hover:text-red-300 font-medium">Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-sm text-zinc-500 mb-4">No floor plans added yet</p>
                      <input type="text" placeholder="Paste floor plan image URL and press Enter..." className="w-full max-w-md mx-auto px-4 py-2.5 rounded-lg text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
                        onKeyDown={async (e) => { if (e.key === 'Enter' && e.target.value.trim()) { const url = e.target.value.trim(); const newPlan = { id: Date.now().toString(), url, type: 'image', name: 'Floor Plan' }; setSelectedListingForEdit({...listing, floor_plans: [...(listing?.floor_plans || []), newPlan]}); e.target.value = ''; }}} />
                    </div>
                  )}
                </div>
              )}

              {/* 3D Tours Tab */}
              {mediaTab === 'tours3d' && (
                <div className="p-6 space-y-4">
                  {(listing?.tours_3d || []).map((tour, index) => (
                    <div key={tour.id || index} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: t.cardInner, border: `1px solid ${t.border}` }}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(194,166,122,0.1)' }}>
                        <svg className="w-5 h-5" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-200 truncate">{tour.name || `3D Tour ${index + 1}`}</p>
                        <p className="text-xs text-zinc-500 truncate">{tour.url}</p>
                      </div>
                      <a href={tour.url} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors" style={{ background: t.accentBg, color: gold }}>View</a>
                      <button onClick={() => { const updated = (listing?.tours_3d || []).filter((_, i) => i !== index); setSelectedListingForEdit({...listing, tours_3d: updated}); }} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <input type="text" id="tour3d-name" placeholder="Name" className="w-1/4 px-3 py-2 rounded-lg text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-500" />
                    <input type="text" id="tour3d-url" placeholder="Paste 3D tour URL..." className="flex-1 px-3 py-2 rounded-lg text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-500" />
                    <button onClick={() => { const n = document.getElementById('tour3d-name'); const u = document.getElementById('tour3d-url'); if (u.value.trim()) { const newTour = { id: Date.now().toString(), url: u.value.trim(), name: n.value.trim() || '3D Tour' }; setSelectedListingForEdit({...listing, tours_3d: [...(listing?.tours_3d || []), newTour]}); n.value = ''; u.value = ''; }}} className="px-4 py-2 rounded-lg text-sm font-medium" style={{ background: gold, color: darkMode ? '#1a1a1a' : '#ffffff' }}>Add</button>
                  </div>
                </div>
              )}

              {/* Files Tab */}
              {mediaTab === 'files' && (
                <div className="p-6">
                  {files.length > 0 ? (
                    <>
                      {renderTabToolbar()}
                      {bulkMode && renderBulkActionsBar()}
                      <div className="space-y-2">
                        {files.map((media, index) => {
                          const isSelected = selectedIds.has(media.id);
                          return (
                          <div
                            key={media.id}
                            draggable={!bulkMode}
                            onDragStart={(e) => !bulkMode && handleDragStart(e, mediaFiles.indexOf(media))}
                            onDragOver={(e) => !bulkMode && handleDragOver(e, mediaFiles.indexOf(media))}
                            onDragEnter={(e) => !bulkMode && handleDragEnter(e, mediaFiles.indexOf(media))}
                            onDragLeave={(e) => !bulkMode && handleDragLeave(e)}
                            onDrop={(e) => !bulkMode && handleDrop(e, mediaFiles.indexOf(media))}
                            onDragEnd={() => { setDraggingIndex(null); setDragOverIndex(null); }}
                            onClick={() => bulkMode && toggleImageSelected(media.id)}
                            data-testid={bulkMode ? `bulk-file-row-${media.id}` : undefined}
                            className={`flex items-center gap-3 p-3 rounded-lg transition-all ${bulkMode ? 'cursor-pointer' : 'cursor-move'} ${
                              draggingIndex === mediaFiles.indexOf(media) ? 'opacity-40' :
                              dragOverIndex === mediaFiles.indexOf(media) ? 'ring-2 ring-emerald-500' :
                              bulkMode && isSelected ? 'ring-2 ring-indigo-400' : ''
                            }`}
                            style={{ background: t.cardInner, border: `1px solid ${t.border}` }}
                          >
                            {bulkMode && (
                              <div
                                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  isSelected
                                    ? 'bg-indigo-500 border-indigo-400'
                                    : 'bg-transparent border-zinc-600'
                                }`}
                              >
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                )}
                              </div>
                            )}
                            <span className="text-[10px] font-mono text-zinc-600">#{index + 1}</span>
                            <svg className="w-5 h-5 text-zinc-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            <div className="flex-1 min-w-0"><p className="text-sm text-zinc-200 truncate">{media.original_filename}</p><p className="text-xs text-zinc-600">{media.mime_type}</p></div>
                            {!bulkMode && (
                              <>
                                <button onClick={(e) => { e.stopPropagation(); const l = document.createElement('a'); l.href = `${BACKEND_URL}/api/media/${media.id}`; l.download = media.original_filename; document.body.appendChild(l); l.click(); document.body.removeChild(l); }} className="p-1.5 rounded hover:bg-zinc-700 text-zinc-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg></button>
                                <button onClick={(e) => { e.stopPropagation(); setMediaToDelete(media); setShowDeleteConfirm(true); }} className="p-1.5 rounded hover:bg-red-500/20 text-zinc-400 hover:text-red-400"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                              </>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </>
                  ) : <div className="text-center py-12"><p className="text-sm text-zinc-500">No files uploaded yet</p></div>}
                </div>
              )}
            </div>

            {/* Marketing & Property Websites */}
            <div className="rounded-xl p-6" style={{ background: t.card, border: `1px solid ${t.border}` }}>
              <h2 className="text-lg font-semibold text-zinc-50 tracking-tight mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                Marketing Websites
              </h2>
              {listing.property_website_slug ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { name: 'Flow', desc: 'Clean, elegant with smooth transitions', path: '', btn: gold, img: 'https://static.prod-images.emergentagent.com/jobs/36421a6c-cc6c-4173-8ff6-9e520ad87a38/images/cb17f1b5e03bae2fb8174e4df106267ace6d86d403ecc5655805d352f7bce2bc.png' },
                      { name: 'Dark Sleek', desc: 'Bold, contemporary dark theme', path: '/modern', btn: '#18181b', img: 'https://static.prod-images.emergentagent.com/jobs/36421a6c-cc6c-4173-8ff6-9e520ad87a38/images/f180c5c6531250a4b6ca9cfe891bcaae51e759bc3e750f040d03c87d29acdc93.png' },
                      { name: 'Sophisticated', desc: 'Magazine editorial style', path: '/golden', btn: '#C4704F', img: 'https://static.prod-images.emergentagent.com/jobs/36421a6c-cc6c-4173-8ff6-9e520ad87a38/images/fd04deb7b0291d61d67194bc4b73b38586a39c83f2c534a3bea1e28f028ea81d.png' },
                    ].map(tmpl => (
                      <div key={tmpl.name} className="rounded-xl overflow-hidden group transition-transform hover:-translate-y-1" style={{ background: t.cardInner, border: `1px solid ${t.border}` }}>
                        <div className="aspect-[16/10] overflow-hidden"><img src={tmpl.img} alt={tmpl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                        <div className="p-4">
                          <h3 className="font-semibold text-zinc-100 text-sm">{tmpl.name}</h3>
                          <p className="text-xs text-zinc-500 mt-1 mb-3">{tmpl.desc}</p>
                          <div className="flex gap-2">
                            <button onClick={() => window.open(`/property/${listing.property_website_slug}${tmpl.path}`, '_blank')} className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold text-white transition-colors" style={{ background: tmpl.btn }}>Preview</button>
                            <button onClick={async () => { const url = `${window.location.origin}/property/${listing.property_website_slug}${tmpl.path}`; try { await navigator.clipboard.writeText(url); showToast('Link copied!', 'success'); } catch { const ta = document.createElement('textarea'); ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); showToast('Link copied!', 'success'); }}} className="p-2 rounded-lg text-zinc-400 hover:text-zinc-200 transition-colors" style={{ border: `1px solid ${t.border}` }} title="Copy Link">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-zinc-600 text-center mt-4">Share these links with clients to showcase the property</p>
                </>
              ) : (
                <button onClick={async () => { try { const r = await axios.post(`${API}/appointments/${listing.id}/generate-website-slug`); setSelectedListingForEdit({...listing, property_website_slug: r.data.slug}); setAppointments(appointments.map(a => a.id === listing.id ? {...a, property_website_slug: r.data.slug} : a)); } catch (e) { showToast('Failed to generate website link', 'error'); }}} className="w-full px-4 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors" style={{ background: gold, color: darkMode ? '#1a1a1a' : '#ffffff' }} data-testid="generate-website-btn">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  Generate Property Website Links
                </button>
              )}

              {/* Property Details (collapsible) */}
              <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${t.border}` }}>
                <button onClick={() => setPropertyDetailsExpanded(!propertyDetailsExpanded)} className="flex items-center gap-2 w-full text-left" data-testid="property-details-toggle">
                  <svg className={`w-4 h-4 text-zinc-500 transition-transform ${propertyDetailsExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  <span className="text-sm font-medium text-zinc-300">Property Details</span>
                </button>
                {propertyDetailsExpanded && (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {[
                        { label: 'SqFt', key: 'property_sqft', type: 'number', ph: '2500' },
                        { label: 'Beds', key: 'property_beds', type: 'number', ph: '3' },
                        { label: 'Baths', key: 'property_baths', type: 'number', ph: '2.5', step: '0.5' },
                        { label: 'Built', key: 'property_year_built', type: 'number', ph: '2020' },
                        { label: 'Lot Size', key: 'property_lot_size', type: 'text', ph: '0.5 acres' },
                        { label: 'Parking', key: 'property_parking', type: 'text', ph: '2 car garage' },
                      ].map(f => (
                        <div key={f.key}>
                          <label className="block text-xs text-zinc-500 mb-1">{f.label}</label>
                          <input type={f.type} step={f.step} value={listing[f.key] || ''} onChange={(e) => setSelectedListingForEdit({...listing, [f.key]: e.target.value})} className="w-full px-3 py-2 rounded-lg text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-500" placeholder={f.ph} />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Description</label>
                      <textarea value={listing.property_description || ''} onChange={(e) => setSelectedListingForEdit({...listing, property_description: e.target.value})} rows={3} className="w-full px-3 py-2 rounded-lg text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-500" placeholder="Property description..." />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-500 mb-1">Price</label>
                      <input type="text" value={listing.property_price || ''} onChange={(e) => setSelectedListingForEdit({...listing, property_price: e.target.value})} className="w-full px-3 py-2 rounded-lg text-sm bg-zinc-900 border border-zinc-700 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-zinc-500" placeholder="$450,000" />
                    </div>
                    <button onClick={async () => { try { await axios.put(`${API}/appointments/${listing.id}`, { property_sqft: listing.property_sqft, property_beds: listing.property_beds, property_baths: listing.property_baths, property_year_built: listing.property_year_built, property_lot_size: listing.property_lot_size, property_parking: listing.property_parking, property_description: listing.property_description, property_price: listing.property_price }); showToast('Property details saved!', 'success'); const r = await axios.get(`${API}/appointments/${listing.id}`); setSelectedListingForEdit(r.data); } catch (e) { showToast('Failed to save: ' + (e.response?.data?.detail || e.message), 'error'); }}} className="px-5 py-2 rounded-lg text-sm font-semibold transition-colors" style={{ background: gold, color: darkMode ? '#1a1a1a' : '#ffffff' }} data-testid="save-property-details-btn">
                      Save Details
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Clients, Tasks, Appointments */}
          <div className="xl:col-span-3 space-y-6">

            {/* Clients */}
            <div className="rounded-xl p-5" style={{ background: t.card, border: `1px solid ${t.border}` }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-zinc-50 tracking-tight flex items-center gap-2">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                  Clients
                </h3>
                <button onClick={() => { setSelectedClientToAdd(''); setShowAddClientModal(true); }} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: t.accentBg, color: gold }} data-testid="add-client-btn">+ Add</button>
              </div>
              <div className="space-y-2">
                {/* Primary Client */}
                {(() => {
                  const target = primaryClient || clients.find(c => c.id === listing.client_id) || (listing.client_name ? clients.find(c => c.name === listing.client_name) : null);
                  const canEdit = !!target;
                  return (
                    <div
                      onClick={() => { if (target) editClient(target); }}
                      role={canEdit ? 'button' : undefined}
                      tabIndex={canEdit ? 0 : undefined}
                      onKeyDown={canEdit ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editClient(target); } } : undefined}
                      data-testid="primary-client-row"
                      className={`flex items-center gap-3 p-3 rounded-lg ${canEdit ? 'cursor-pointer transition-colors hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-amber-400/40' : ''}`}
                      style={{ background: t.cardInner, border: `1px solid ${t.border}` }}
                    >
                      {primaryClient?.avatar && clientAvatarUrls[primaryClient.avatar] ? (
                        <img src={clientAvatarUrls[primaryClient.avatar]} alt={primaryClient.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0" style={{ background: 'rgba(194,166,122,0.15)', color: gold }}>{(primaryClient?.name || listing.client_name)?.charAt(0).toUpperCase()}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-zinc-200 truncate">{primaryClient?.name || listing.client_name}</p>
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded" style={{ background: t.accentBg, color: gold }}>Primary</span>
                        </div>
                        {primaryClient?.email && <p className="text-xs text-zinc-500 truncate">{primaryClient.email}</p>}
                      </div>
                    </div>
                  );
                })()}
                {/* Additional Clients */}
                {propertyClients.map((clientId) => {
                  const client = clients.find(c => c.id === clientId);
                  if (!client) return null;
                  return (
                    <div
                      key={clientId}
                      onClick={() => editClient(client)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editClient(client); } }}
                      data-testid={`additional-client-row-${client.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                      style={{ background: t.cardInner, border: `1px solid ${t.border}` }}
                    >
                      {client.avatar && clientAvatarUrls[client.avatar] ? (
                        <img src={clientAvatarUrls[client.avatar]} alt={client.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 text-sm font-semibold flex-shrink-0">{client.name?.charAt(0).toUpperCase()}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-200 truncate">{client.name}</p>
                        {client.email && <p className="text-xs text-zinc-500 truncate">{client.email}</p>}
                      </div>
                      <button
                        onClick={async (e) => { e.stopPropagation(); try { const updated = propertyClients.filter(id => id !== clientId); await axios.patch(`${API}/appointments/${listing.id}/clients`, { client_ids: updated }); setPropertyClients(updated); } catch (err) { showToast('Failed to remove client', 'error'); }}}
                        className="p-1 rounded hover:bg-red-500/20 text-zinc-600 hover:text-red-400 transition-colors"
                        title="Remove from this listing"
                        data-testid={`remove-additional-client-${client.id}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tasks */}
            <div className="rounded-xl" style={{ background: t.card, border: `1px solid ${t.border}` }}>
              <div className="flex items-center justify-between p-5">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                  <h3 className="text-sm font-semibold text-zinc-50 tracking-tight">Tasks</h3>
                  {listingTasks.length > 0 && <span className={`text-xs font-medium ${completedTasks === listingTasks.length ? 'text-emerald-400' : 'text-zinc-500'}`}>{completedTasks}/{listingTasks.length}</span>}
                </div>
                <button onClick={() => { setEditingTask(null); setTaskForm({ appointment_id: listing.id, propertySearch: `${listing.property_address} - ${listing.client_name}`, title: '', description: '', due_date: '', assigned_to: '', priority: 'medium', status: 'pending' }); setShowTaskForm(true); }} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: t.accentBg, color: gold }} data-testid="add-task-btn">+ Add</button>
              </div>
              {listingTasks.length > 0 && (
                <div className="px-5 pb-5 space-y-2">
                  {listingTasks.map(task => {
                    const isOverdue = new Date(task.due_date) < new Date() && task.status !== 'completed';
                    const member = teamMembers.find(m => m.id === task.assigned_to);
                    return (
                      <div
                        key={task.id}
                        onClick={() => editTask(task)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); editTask(task); } }}
                        data-testid={`listing-task-row-${task.id}`}
                        className="flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-zinc-800/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                        style={{ background: t.cardInner, border: `1px solid ${t.border}` }}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-200">{task.title}</p>
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {member && (
                              <span className="text-[10px] text-zinc-500">{member.name}</span>
                            )}
                            <span className={`text-[10px] ${isOverdue ? 'text-red-400' : 'text-zinc-600'}`}>{moment(task.due_date).format('MMM D')}</span>
                            <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${task.priority === 'high' ? 'bg-red-500/10 text-red-400' : task.priority === 'medium' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>{task.priority}</span>
                          </div>
                        </div>
                        <select
                          value={task.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { e.stopPropagation(); updateTaskStatus(task.id, e.target.value); }}
                          className={`px-2 py-1 text-[10px] font-semibold rounded-lg border cursor-pointer bg-transparent ${task.status === 'completed' ? 'text-emerald-400 border-emerald-500/20' : task.status === 'in_progress' ? 'text-blue-400 border-blue-500/20' : 'text-amber-400 border-amber-500/20'}`}
                          data-testid={`listing-task-status-${task.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Appointments */}
            <div className="rounded-xl" style={{ background: t.card, border: `1px solid ${t.border}` }}>
              <div className="flex items-center justify-between p-5">
                <h3 className="text-sm font-semibold text-zinc-50 tracking-tight flex items-center gap-2">
                  <svg className="w-4 h-4" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Appointments
                </h3>
                <button onClick={() => { setEditingAppointment(null); setAppointmentForm({ client_id: listing.client_id, scheduled_date: '', scheduled_time: '', property_address: listing.property_address, photographer_id: '', duration_minutes: 120, notes: '', property_sqft: listing.property_sqft || '' }); setSelectedProducts([]); setShowAppointmentForm(true); }} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" style={{ background: t.accentBg, color: gold }} data-testid="add-appointment-btn">+ Add</button>
              </div>
              <div className="px-5 pb-5 space-y-2">
                {/* Current */}
                <div
                  onClick={async () => { if (products.length === 0) { await fetchSettings(); await new Promise(r => setTimeout(r, 200)); } setEditingAppointment(listing); setAppointmentForm({ client_id: listing.client_id, scheduled_date: listing.scheduled_date, scheduled_time: listing.scheduled_time, property_address: listing.property_address, photographer_id: listing.photographer_id || '', duration_minutes: listing.duration_minutes, notes: listing.notes || '', property_sqft: listing.property_size || listing.property_sqft || '' }); setSelectedProducts(listing.selected_products || []); setAppointmentClientSearchQuery(listing.client_name || ''); setShowAppointmentForm(true); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.currentTarget.click(); } }}
                  data-testid="edit-current-appointment-btn"
                  className="p-3 rounded-lg cursor-pointer transition-colors hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                  style={{ background: 'rgba(194,166,122,0.05)', border: '1px solid rgba(194,166,122,0.15)' }}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded" style={{ background: gold, color: darkMode ? '#1a1a1a' : '#ffffff' }}>Current</span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border ${listing.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : listing.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'}`}>{listing.status}</span>
                  </div>
                  <p className="text-sm font-medium text-zinc-200">{formatTableDate(listing.scheduled_date)} at {formatTableTime(listing.scheduled_time)}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{listing.photographer_name || 'Unassigned'}</p>
                </div>
                {/* Other appointments */}
                {appointments.filter(a => a.property_address === listing.property_address && a.id !== listing.id).sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)).map(apt => (
                  <div
                    key={apt.id}
                    onClick={() => { setEditingAppointment(apt); setAppointmentForm({ client_id: apt.client_id, scheduled_date: apt.scheduled_date, scheduled_time: apt.scheduled_time, property_address: apt.property_address, photographer_id: apt.photographer_id || '', duration_minutes: apt.duration_minutes, notes: apt.notes || '', property_sqft: apt.property_size || apt.property_sqft || '' }); setSelectedProducts(apt.selected_products || []); setAppointmentClientSearchQuery(apt.client_name || ''); if (apt.selected_products?.length > 0 && (apt.property_size || apt.property_sqft)) calculateAppointmentPricing(apt.selected_products, apt.property_size || apt.property_sqft); setShowAppointmentForm(true); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.currentTarget.click(); } }}
                    data-testid={`edit-appointment-${apt.id}`}
                    className="p-3 rounded-lg cursor-pointer transition-colors hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                    style={{ background: t.cardInner, border: `1px solid ${t.border}` }}
                  >
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border ${apt.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : apt.status === 'scheduled' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'}`}>{apt.status}</span>
                    <p className="text-sm font-medium text-zinc-200 mt-1.5">{formatTableDate(apt.scheduled_date)} at {formatTableTime(apt.scheduled_time)}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{apt.photographer_name || 'Unassigned'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity Log */}
            <div className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: t.card, border: `1px solid ${t.border}` }}>
              <div className="flex items-center gap-2 mb-4">
                <svg className="w-4 h-4" style={{ color: gold }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <h3 className="text-sm font-semibold text-zinc-50 tracking-wide uppercase">Activity</h3>
              </div>
              <div className={!darkMode ? '' : 'activity-on-dark'}>
                <ActivityLog entityType="listing" entityId={listing.id} />
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Bulk delete confirmation */}
      {showBulkDeleteConfirm && (
        <div
          data-testid="bulk-delete-confirm-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => !bulkBusy && setShowBulkDeleteConfirm(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: t.card, border: `1px solid ${t.border}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-zinc-100">Delete {selectedIds.size} {(() => {
                const noun = mediaTab === 'videos' ? 'video' : mediaTab === 'files' ? 'file' : 'image';
                return selectedIds.size === 1 ? noun : `${noun}s`;
              })()}?</h3>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">
              This action cannot be undone. The selected items will be permanently removed from this listing.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                disabled={bulkBusy}
                onClick={() => setShowBulkDeleteConfirm(false)}
                data-testid="bulk-delete-cancel-btn"
                className="px-4 py-2 text-sm font-medium rounded-lg text-zinc-300 hover:bg-white/5 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={bulkBusy}
                onClick={handleBulkDelete}
                data-testid="bulk-delete-confirm-btn"
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-40 transition-colors"
              >
                {bulkBusy ? 'Deleting…' : `Delete ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditListingPage;
